import {
  Activity,
  ArrowRight,
  Bot,
  Clock,
  Compass,
  Eye,
  Gauge,
  LayoutDashboard,
  MessageCircle,
  Phone,
  PlayCircle,
  ShieldCheck,
  Target,
  TrendingDown,
  TrendingUp,
  User,
  Users,
  Wallet,
  Zap,
} from "lucide-react";

const GOLD = "#E8B339";

/* ----------------------------- Animated Meda Eye -----------------------------
 * Mirrors the eye used on the landing/login page so the /saas hero shares the
 * same brand mark. Self-contained (own keyframes) so it never affects the rest
 * of the SaaS page theme. */
function MedaEye() {
  return (
    <div
      className="relative mx-auto aspect-[5/3] w-64 sm:w-80 lg:w-[26rem]"
      data-testid="meda-ai-hero-eye"
    >
      {/* Orbital rings */}
      <div className="pointer-events-none absolute inset-0">
        <div className="meda-spin absolute inset-0" style={{ animationDuration: "16s" }}>
          <div className="meda-ellipse" style={{ transform: "scaleY(0.34) rotate(-18deg)" }} />
        </div>
        <div className="meda-spin-rev absolute inset-0" style={{ animationDuration: "24s" }}>
          <div className="meda-ellipse" style={{ transform: "scaleY(0.28) rotate(14deg)", opacity: 0.5 }} />
        </div>
      </div>

      {/* Twinkling particles */}
      {[
        { t: "12%", l: "18%", d: "0s" },
        { t: "20%", l: "82%", d: "0.6s" },
        { t: "78%", l: "26%", d: "1.1s" },
        { t: "70%", l: "74%", d: "0.3s" },
        { t: "46%", l: "6%", d: "0.9s" },
        { t: "50%", l: "94%", d: "1.4s" },
      ].map((p, i) => (
        <span
          key={i}
          className="meda-twinkle absolute h-1 w-1 rounded-full bg-amber-300"
          style={{ top: p.t, left: p.l, animationDelay: p.d, boxShadow: `0 0 6px ${GOLD}` }}
        />
      ))}

      <svg viewBox="0 0 400 240" className="relative h-full w-full" style={{ transformOrigin: "200px 120px" }}>
        <defs>
          <radialGradient id="medaHeroIris" cx="50%" cy="45%" r="60%">
            <stop offset="0%" stopColor="#9CC6FF" />
            <stop offset="38%" stopColor="#2F7DF6" />
            <stop offset="72%" stopColor="#1437A6" />
            <stop offset="100%" stopColor="#06122E" />
          </radialGradient>
          <radialGradient id="medaHeroIrisGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="medaHeroGold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FCE7A1" />
            <stop offset="50%" stopColor={GOLD} />
            <stop offset="100%" stopColor="#9A6B12" />
          </linearGradient>
          <filter id="medaHeroGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3.2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* layered almond lids */}
        <g filter="url(#medaHeroGlow)" fill="none" stroke="url(#medaHeroGold)">
          <path d="M40,120 Q200,18 360,120 Q200,222 40,120 Z" strokeWidth="3.5" />
          <path d="M70,120 Q200,46 330,120 Q200,194 70,120 Z" strokeWidth="2" opacity="0.7" />
          <path d="M96,120 Q200,66 304,120 Q200,174 96,120 Z" strokeWidth="1.2" opacity="0.45" />
        </g>

        {/* iris */}
        <g className="meda-pulse" style={{ transformOrigin: "200px 120px" }}>
          <circle cx="200" cy="120" r="78" fill="url(#medaHeroIrisGlow)" />
          <circle cx="200" cy="120" r="54" fill="url(#medaHeroIris)" />
          <circle cx="200" cy="120" r="58" fill="none" stroke="url(#medaHeroGold)" strokeWidth="1.4" opacity="0.85" />
          <circle cx="200" cy="120" r="64" fill="none" stroke="url(#medaHeroGold)" strokeWidth="0.8" opacity="0.5" />
          <circle cx="200" cy="120" r="24" fill="#020611" />
          <circle cx="188" cy="106" r="6" fill="#fff" opacity="0.85" />
        </g>
      </svg>
    </div>
  );
}

const ORBIT_NODES = [
  { icon: Users, label: "Counsellors", top: "1%", left: "50%" },
  { icon: User, label: "Leads", top: "20%", left: "90%" },
  { icon: LayoutDashboard, label: "CRM", top: "52%", left: "99%" },
  { icon: TrendingUp, label: "Revenue", top: "82%", left: "88%" },
  { icon: Phone, label: "Calls", top: "99%", left: "50%" },
  { icon: MessageCircle, label: "WhatsApp", top: "62%", left: "3%", green: true },
];

function OrbitGraphic() {
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[26rem] sm:max-w-[30rem]" data-testid="meda-ai-hero-orbit">
      {/* orbit rings */}
      <div className="pointer-events-none absolute inset-[10%] rounded-full border border-amber-400/35" />
      <div className="meda-spin pointer-events-none absolute inset-[10%] rounded-full border border-dashed border-amber-400/25" style={{ animationDuration: "40s" }} />
      <div className="pointer-events-none absolute inset-[24%] rounded-full border border-amber-400/20" />

      {/* centered eye */}
      <div className="absolute inset-0 flex items-center justify-center">
        <MedaEye />
      </div>

      {/* orbiting nodes */}
      {ORBIT_NODES.map((n) => (
        <div
          key={n.label}
          className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1.5"
          style={{ top: n.top, left: n.left }}
          data-testid={`meda-ai-orbit-${n.label.toLowerCase()}`}
        >
          <span
            className={`flex h-12 w-12 items-center justify-center rounded-2xl border bg-white ${
              n.green
                ? "border-[#25D366]/60 shadow-[0_6px_18px_-6px_rgba(37,211,102,0.6)]"
                : "border-amber-300 shadow-[0_6px_18px_-6px_rgba(232,179,57,0.55)]"
            }`}
          >
            <n.icon className={`h-5 w-5 ${n.green ? "text-[#25D366]" : "text-[#12245B]"}`} />
          </span>
          <span className="whitespace-nowrap text-[11px] font-semibold text-[#12245B]">{n.label}</span>
        </div>
      ))}
    </div>
  );
}

const DOES = [
  { icon: Activity, title: "Monitors Every Lead Activity", desc: "Never lose track of hot leads, pending enquiries or missed opportunities." },
  { icon: Clock, title: "Tracks Every Follow-Up", desc: "Ensure every student and parent receives timely follow-ups." },
  { icon: Users, title: "Keeps Your Team Accountable", desc: "Monitor counsellor productivity, task completion and performance in one place." },
  { icon: TrendingDown, title: "Detects Revenue Leakage", desc: "Identify missed admissions opportunities before they impact your business." },
  { icon: Gauge, title: "Provides Real-Time Insights", desc: "Know exactly what's happening across your consultancy at any moment." },
  { icon: Compass, title: "Guides Your Team to Better Results", desc: "Meda recommends the next best action to help increase conversions and admissions." },
];

const WHY = [
  { icon: Eye, label: "Complete Visibility" },
  { icon: TrendingUp, label: "More Admissions" },
  { icon: Target, label: "Better Team Performance" },
  { icon: Wallet, label: "Reduced Revenue Leakage" },
  { icon: Zap, label: "Faster Decision Making" },
  { icon: Bot, label: "AI-Powered Monitoring 24/7" },
];

export default function MedaAiHero({ onBookDemo }) {
  return (
    <section
      data-testid="meda-ai-hero"
      className="relative overflow-hidden bg-[#E7F0FF] text-[#0A2540]"
    >
      <MedaHeroStyles />

      {/* ambient glows */}
      <div className="pointer-events-none absolute -left-40 top-10 h-96 w-96 rounded-full bg-amber-500/10 blur-[120px]" />
      <div className="pointer-events-none absolute -right-32 top-1/3 h-96 w-96 rounded-full bg-blue-600/10 blur-[120px]" />
      {/* bottom gold shimmer */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 opacity-70">
        <svg viewBox="0 0 1440 200" className="h-full w-full" preserveAspectRatio="none">
          <defs>
            <linearGradient id="medaHeroWave" x1="0" x2="1">
              <stop offset="0%" stopColor={GOLD} stopOpacity="0" />
              <stop offset="50%" stopColor={GOLD} stopOpacity="0.55" />
              <stop offset="100%" stopColor={GOLD} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M0,140 C320,40 680,200 1440,90 L1440,200 L0,200 Z" fill="url(#medaHeroWave)" opacity="0.35" />
          <path d="M0,160 C400,80 900,210 1440,120" fill="none" stroke="url(#medaHeroWave)" strokeWidth="2" />
        </svg>
      </div>

      {/* ===================== LIGHT HERO ===================== */}
      <div className="relative overflow-hidden bg-gradient-to-b from-white via-[#F4F8FF] to-[#E7F0FF] text-[#0A2540]">
        {/* soft ambient glows (light) */}
        <div className="pointer-events-none absolute -left-32 top-0 h-80 w-80 rounded-full bg-blue-300/25 blur-[120px]" />
        <div className="pointer-events-none absolute -right-24 top-1/4 h-96 w-96 rounded-full bg-amber-300/20 blur-[130px]" />

        <div className="relative z-10 mx-auto max-w-7xl px-6 pb-16 pt-7 lg:px-10 lg:pb-24">
          {/* Top nav */}
          <nav data-testid="meda-ai-hero-nav" className="flex items-center justify-between">
            <a href="#meda-top" className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full border border-blue-300 bg-blue-50">
                <Eye className="h-5 w-5 text-blue-600" />
              </span>
              <span className="font-display text-xl font-bold tracking-tight text-[#0A2540]">
                Meda <span className="text-amber-500">AI</span>
              </span>
            </a>
            <div className="hidden items-center gap-8 md:flex">
              <a href="#product" data-testid="meda-ai-nav-product" className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0A2540]/70 transition-colors hover:text-blue-600">Product</a>
              <a href="#features" data-testid="meda-ai-nav-features" className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0A2540]/70 transition-colors hover:text-blue-600">Features</a>
              <a href="#faq" data-testid="meda-ai-nav-faq" className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0A2540]/70 transition-colors hover:text-blue-600">FAQ</a>
            </div>
            <div className="flex items-center gap-3">
              <a
                href="/admin/login"
                data-testid="meda-ai-nav-login"
                className="rounded-xl border border-blue-400/70 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-blue-700 transition-colors hover:bg-blue-50"
              >
                Login
              </a>
              <button
                type="button"
                onClick={onBookDemo}
                data-testid="meda-ai-nav-book-demo"
                className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#F6D277] via-[#E8B339] to-[#C9971F] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#1A1206] shadow-[0_8px_24px_-8px_rgba(232,179,57,0.7)] transition-transform hover:scale-[1.03]"
              >
                Book Demo <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </nav>

          {/* Hero */}
          <div id="meda-top" className="mt-16 grid items-center gap-10 lg:mt-56 lg:grid-cols-[1.05fr_0.95fr] lg:gap-12">
            {/* Left — copy */}
            <div className="meda-rise">
              <h1 className="font-display text-5xl font-extrabold leading-[1.04] tracking-tight sm:text-6xl lg:text-[4.25rem]">
                <span className="text-[#12245B]">Stop Losing<br />Admissions.</span>
                <br />
                <span className="text-amber-500">Start Growing<br />Revenue.</span>
              </h1>

              {/* gold diamond divider */}
              <div className="mt-8 flex items-center gap-2">
                <span className="h-px w-16 bg-gradient-to-r from-amber-400 to-amber-400/0" />
                <span className="h-2 w-2 rotate-45 bg-amber-400" />
              </div>

              {/* Sub headline */}
              <p className="mt-6 font-display text-xl font-bold tracking-tight text-[#12245B] sm:text-2xl">
                World's 1st <span className="text-[#2F6BFF]">AI Powered</span> Admission Growth OS
              </p>

              {/* Built for */}
              <div className="mt-6 flex items-center gap-2.5 text-sm text-slate-600">
                <ShieldCheck className="h-5 w-5 shrink-0 text-blue-600" />
                <span>
                  Built for <span className="font-bold text-amber-600">Study Abroad &amp; MBBS</span> Consultancies
                </span>
              </div>

              {/* Orbit graphic — mobile only, above CTAs */}
              <div className="mt-40 lg:hidden">
                <OrbitGraphic />
              </div>

              {/* CTAs — hidden on mobile */}
              <div className="mt-9 hidden flex-wrap gap-4 sm:flex">
                <button
                  type="button"
                  onClick={onBookDemo}
                  data-testid="meda-ai-hero-cta"
                  className="inline-flex h-14 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#F6D277] via-[#E8B339] to-[#C9971F] px-7 text-sm font-bold uppercase tracking-[0.12em] text-[#1A1206] shadow-[0_10px_30px_-8px_rgba(232,179,57,0.7)] transition-transform hover:scale-[1.02] active:scale-[0.99]"
                >
                  See Meda AI in Action <ArrowRight className="h-5 w-5" />
                </button>
                <a
                  href="#product"
                  data-testid="meda-ai-hero-cta-tour"
                  className="inline-flex h-14 items-center justify-center gap-2 rounded-xl border border-blue-400/70 px-7 text-sm font-bold uppercase tracking-[0.12em] text-blue-700 transition-colors hover:bg-blue-50"
                >
                  <PlayCircle className="h-5 w-5" /> Watch 2 Min Tour
                </a>
              </div>
            </div>

            {/* Right — orbit graphic (desktop only) */}
            <div className="meda-rise hidden lg:block">
              <OrbitGraphic />
            </div>
          </div>
        </div>
      </div>

      {/* ===================== DARK SECTIONS (unchanged) ===================== */}
      <div className="relative z-10 mx-auto max-w-5xl px-6 pb-20 pt-20 lg:pb-28">

        {/* What Meda AI Does For You */}
        <div className="mt-20">
          <div className="flex flex-col items-center text-center">
            <span className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-600">
              What Meda AI Does For You
            </span>
            <div className="mt-3 h-px w-40 bg-gradient-to-r from-transparent via-amber-400 to-transparent" />
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {DOES.map((d) => (
              <div
                key={d.title}
                data-testid={`meda-ai-does-${d.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                className="group rounded-2xl border border-amber-300/60 bg-white p-6 shadow-[0_10px_30px_-18px_rgba(232,179,57,0.55)] transition-all hover:border-amber-400 hover:shadow-[0_16px_40px_-16px_rgba(232,179,57,0.6)]"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-amber-300 bg-amber-50">
                  <d.icon className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="mt-4 font-display text-lg font-bold text-[#0A2540]">{d.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Why Consultancy Owners Choose Meda */}
        <div className="mt-20">
          <div className="flex flex-col items-center text-center">
            <span className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-600">
              Why Consultancy Owners Choose Meda
            </span>
            <div className="mt-3 h-px w-40 bg-gradient-to-r from-transparent via-amber-400 to-transparent" />
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {WHY.map((w) => (
              <div
                key={w.label}
                data-testid={`meda-ai-why-${w.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                className="flex items-center gap-3 rounded-xl border border-amber-200 bg-white px-5 py-4 shadow-[0_8px_24px_-18px_rgba(232,179,57,0.5)]"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-amber-300 bg-amber-50">
                  <w.icon className="h-5 w-5 text-amber-600" />
                </div>
                <span className="text-sm font-semibold text-[#0A2540]">{w.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Closing CTA */}
        <div className="mt-20 rounded-3xl border border-amber-300 bg-white p-10 text-center shadow-[0_20px_60px_-25px_rgba(232,179,57,0.5)]">
          <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-[#0A2540] sm:text-4xl">
            Stop Managing Blindly.
            <br />
            <span className="text-amber-500">Start Growing with Confidence.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate-600">
            Book a Free Demo and see how Meda AI can transform your admissions operations.
          </p>
          <button
            type="button"
            onClick={onBookDemo}
            data-testid="meda-ai-hero-cta-bottom"
            className="mt-7 inline-flex h-14 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#F6D277] via-[#E8B339] to-[#C9971F] px-8 text-base font-bold text-[#1A1206] shadow-[0_10px_30px_-8px_rgba(232,179,57,0.6)] transition-transform hover:scale-[1.02] active:scale-[0.99]"
          >
            Book a Free Demo <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </section>
  );
}

/* scoped keyframes — identical to the login page eye animations */
function MedaHeroStyles() {
  return (
    <style>{`
      @keyframes meda-spin { to { transform: rotate(360deg); } }
      @keyframes meda-spin-rev { to { transform: rotate(-360deg); } }
      .meda-spin { animation: meda-spin linear infinite; }
      .meda-spin-rev { animation: meda-spin-rev linear infinite; }
      .meda-ellipse {
        position: absolute; inset: 6%;
        border: 1px solid rgba(232,179,57,0.55);
        border-radius: 50%;
        box-shadow: 0 0 12px rgba(232,179,57,0.25);
      }
      @keyframes meda-twinkle { 0%,100%{opacity:.15; transform:scale(.7);} 50%{opacity:1; transform:scale(1.2);} }
      .meda-twinkle { animation: meda-twinkle 2.6s ease-in-out infinite; }
      @keyframes meda-pulse { 0%,100%{ transform: scale(1); opacity:.92;} 50%{ transform: scale(1.04); opacity:1;} }
      .meda-pulse { animation: meda-pulse 3.4s ease-in-out infinite; }
      @keyframes meda-rise { 0%{opacity:0; transform:translateY(16px);} 100%{opacity:1; transform:translateY(0);} }
      .meda-rise { animation: meda-rise .7s ease both; }
      @media (prefers-reduced-motion: reduce) {
        .meda-spin,.meda-spin-rev,.meda-twinkle,.meda-pulse,.meda-rise { animation: none !important; }
      }
    `}</style>
  );
}
